import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Waves, Fish, MapPin } from "lucide-react";
import { IMAGES, SURF_SERVICES, FISH_SERVICES, CONTACT } from "../mock";
import ServiceCard from "../components/ServiceCard";
import Seo from "../components/Seo";

const CITIES = {
  encinitas: {
    name: "Encinitas",
    short: "Encinitas",
    blurb:
      "Our home break. We launch every lesson and charter from Leucadia and surrounding Encinitas beaches — Beacon's, Stone Steps, Moonlight, D-Street and Swami's.",
  },
  "san-diego": {
    name: "San Diego",
    short: "San Diego",
    blurb:
      "We run surf lessons and fishing charters across all of San Diego County — from La Jolla and Mission Beach to the Bay and the Coronado Islands.",
  },
  "so-cal": {
    name: "Southern California",
    short: "So Cal",
    blurb:
      "Servicing the Southern California coast from Oceanside to Del Mar — we'll meet you where the conditions are best.",
  },
};

const CATEGORIES = {
  surf: {
    title: "Surf Lessons",
    tagline: "All Surf Experiences",
    blurb:
      "Whether you're catching your first wave or sharpening your turns, our certified Encinitas instructors meet you at your level — private, semi-private, group, kids camps, and hotel-delivered sessions.",
    hero: "hzPriv1",
    icon: Waves,
    services: SURF_SERVICES,
    cta: "Book a Surf Lesson",
    basePath: "/surf-lessons",
    steps: [
      {
        n: 1,
        title: "Meet & Connect",
        text:
          "We'll kick things off by getting to know each other — your surfing experience, goals, and what you're hoping to get out of the session. We keep it relaxed, fun, and focused on making sure you feel comfortable and excited to surf.",
      },
      {
        n: 2,
        title: "On-Land Coaching",
        text:
          "Before we hit the water, we'll cover essential techniques on land. You'll learn proper stance, paddling, and pop-up fundamentals, get fitted with the right gear, and make sure everything feels just right.",
      },
      {
        n: 3,
        title: "Let's Surf",
        text:
          "Now it's time to get in the water. Your instructor will guide you wave by wave, helping you build confidence, catch waves, and have an unforgettable surf experience.",
      },
    ],
  },
  fish: {
    title: "Fishing Trips",
    tagline: "All Fishing Charters",
    blurb:
      "From calm bay trips for kids to offshore tuna missions, our USCG-licensed captains know every kelp bed, ledge, and bite window from San Diego Bay to the Coronado Islands.",
    hero: "hzKidsFish1",
    icon: Fish,
    services: FISH_SERVICES,
    cta: "Book a Fishing Trip",
    basePath: "/fishing-trips",
    steps: [
      {
        n: 1,
        title: "Check In & Gear Up",
        text:
          "Meet your captain at the dock. We'll go over the plan, the target species, weather and tide, and get all rods, tackle, bait and safety gear set for the day.",
      },
      {
        n: 2,
        title: "Run to the Fish",
        text:
          "We point the boat at the bite. From inshore kelp beds to offshore high spots and the Coronado Islands, we know where the fish are stacking up this season.",
      },
      {
        n: 3,
        title: "Hook Up & Reel Them In",
        text:
          "Lines in the water. We'll coach you through hookups, fighting fish, and landing them — then we clean and bag the fillets for you to take home.",
      },
    ],
  },
};

const ServicesOverview = ({ category, city: cityKey }) => {
  const data = CATEGORIES[category];
  const city = cityKey ? CITIES[cityKey] : null;
  const Icon = data?.icon;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [category, cityKey]);

  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [category, cityKey]);

  if (!data) return null;

  const heroTitle = city ? `${data.title} in ${city.name}` : data.title;
  const heroBlurb = city ? city.blurb : data.blurb;
  const seoTitle = city
    ? `${data.title} in ${city.name} | Hang Zen Surf + Sea`
    : `${data.title} | Encinitas, San Diego`;
  const seoDesc = city
    ? `${data.title} in ${city.name}, California — ${city.blurb}`
    : data.blurb;
  const seoPath = city ? `${data.basePath}/${cityKey}` : data.basePath;

  const otherCities = Object.entries(CITIES).filter(([k]) => k !== cityKey);

  return (
    <div className="bg-stone-50">
      <Seo title={seoTitle} description={seoDesc} path={seoPath} image={IMAGES[data.hero]} />

      {/* Hero */}
      <section className="relative h-[55vh] min-h-[420px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover"
          style={{
            backgroundImage: `url(${IMAGES[data.hero]})`,
            backgroundPosition: "center 28%",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/30 to-slate-900/80" />
        <div className="relative z-10 h-full flex flex-col justify-end pb-14 max-w-7xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/30 text-white text-xs font-semibold uppercase tracking-[0.25em] mb-4 w-fit">
            <Icon size={14} /> {city ? `${city.short} · ${data.tagline}` : data.tagline}
          </div>
          <h1
            className="text-5xl md:text-7xl text-white drop-shadow-2xl mb-4"
            style={{ fontFamily: "'Pacifico', cursive" }}
          >
            {heroTitle}
          </h1>
          <p className="text-white/90 text-lg max-w-2xl drop-shadow">{heroBlurb}</p>
        </div>
      </section>

      {/* 3-Step Process */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 reveal">
            <div className="text-xs uppercase tracking-[0.3em] text-orange-600 font-semibold mb-3">
              How It Works
            </div>
            <h2
              className="text-3xl md:text-4xl text-[#0b3a5f]"
              style={{ fontFamily: "'Pacifico', cursive" }}
            >
              {category === "surf" ? "Your Surf Lesson Flow" : "Your Charter Day"}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {data.steps.map((s, i) => (
              <div
                key={s.n}
                className="reveal text-center px-2"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div
                  className="text-xl md:text-2xl font-bold text-[#0b3a5f] mb-3 tracking-wide"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {s.n} · {s.title.toUpperCase()}
                </div>
                <div className="w-12 h-0.5 bg-orange-500 mx-auto mb-5" />
                <p className="text-slate-600 leading-relaxed max-w-sm mx-auto">
                  {s.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder credibility section with Scott trophy photo (surf only) */}
      {category === "surf" && (
        <section className="py-16 md:py-20 bg-stone-50">
          <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10 md:gap-14 items-center">
            <div className="reveal relative">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/about-hero.jpg"
                  alt="Scott Hamrick — Founder, Hang Zen Surf + Sea"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "center 25%" }}
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-5 -right-5 w-32 h-32 rounded-full bg-orange-300/40 blur-3xl -z-10" />
              <div className="absolute -top-5 -left-5 w-32 h-32 rounded-full bg-amber-200/40 blur-3xl -z-10" />
            </div>
            <div className="reveal">
              <div className="text-xs uppercase tracking-[0.3em] text-orange-600 font-semibold mb-3">
                Award-Winning Surf School
              </div>
              <h3
                className="text-3xl md:text-4xl text-[#0b3a5f] mb-4 leading-tight"
                style={{ fontFamily: "'Pacifico', cursive" }}
              >
                Learn from Scott &amp; the Hang Zen Crew
              </h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                Founded in 2005 by lifelong waterman Scott Hamrick, Hang Zen Surf + Sea was one of the first three permitted surf schools in North County San Diego. A former competitive surfer, USCG-licensed 100-Ton Master Captain and EMT-certified instructor, Scott has spent decades chasing swell and reading the lineups from La Jolla to Oceanside.
              </p>
              <p className="text-slate-700 leading-relaxed mb-6">
                Every Hang Zen lesson is designed around safety, fundamentals, and that authentic Southern California stoke — whether you're a first-timer or chasing your first overhead drop.
              </p>
              <Link
                to="/about-us"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0b3a5f] hover:bg-[#0e4a78] text-white font-semibold rounded-full transition-colors"
              >
                Meet the Crew <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* All Services Grid */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 reveal">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-orange-600 font-semibold mb-2">
                {data.services.length} Experiences
              </div>
              <h2
                className="text-3xl md:text-4xl text-slate-900"
                style={{ fontFamily: "'Pacifico', cursive" }}
              >
                {city ? `${data.title} in ${city.short}` : "Pick your session"}
              </h2>
            </div>
            <Link
              to="/book-today"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full transition-colors w-fit"
            >
              {data.cta} <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.services.map((s, i) => (
              <div key={s.title} className="reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                <ServiceCard svc={s} index={i} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* City selector */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-8 reveal">
            <div className="text-xs uppercase tracking-[0.3em] text-orange-600 font-semibold mb-2">
              Service Area
            </div>
            <h3
              className="text-2xl md:text-3xl text-[#0b3a5f]"
              style={{ fontFamily: "'Pacifico', cursive" }}
            >
              {data.title} by Location
            </h3>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {Object.entries(CITIES).map(([key, c]) => (
              <Link
                key={key}
                to={`${data.basePath}/${key}`}
                className={`flex items-center justify-between gap-2 px-5 py-4 rounded-2xl border-2 transition-colors ${
                  cityKey === key
                    ? "bg-[#0b3a5f] text-white border-[#0b3a5f]"
                    : "bg-white text-slate-800 border-slate-200 hover:border-[#0b3a5f] hover:text-[#0b3a5f]"
                }`}
              >
                <span className="flex items-center gap-2 font-semibold">
                  <MapPin size={18} />
                  {data.title} in {c.short}
                </span>
                <ArrowRight size={16} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-[#0b3a5f] rounded-3xl p-10 md:p-12 text-center text-white shadow-2xl">
            <h3
              className="text-3xl md:text-4xl mb-3"
              style={{ fontFamily: "'Pacifico', cursive" }}
            >
              Not sure which to pick?
            </h3>
            <p className="text-white/85 mb-6 max-w-lg mx-auto">
              Give us a call or text — we'll match you with the perfect session based on your group, skill level, and tide window.
            </p>
            <div className="flex gap-3 flex-wrap justify-center">
              <a
                href={`tel:${CONTACT.phoneRaw}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full transition-colors"
              >
                Call {CONTACT.phone}
              </a>
              <a
                href={`sms:${CONTACT.phoneRaw}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold rounded-full transition-colors"
              >
                Text Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesOverview;
